// crewbit-admin/src/lib/refundStripeMutation.ts
//
// §Money.HardeningRetirementRefund Phase 5 — Stripe-path refund wrapper.
// Extracted from invoiceMutations.ts per Golden Rule #14 (≤500 LoC).
//
// Calls the refund-invoice-via-stripe edge function via fetch(), passing the
// caller's JWT in the Authorization header. Used by the unified RefundModal
// when invoice.stripe_payment_intent_id is present (Decision #586).

import { supabase } from './supabase';
import { MONEY_MUTATION_EVENT } from '../hooks/useFinancialSummary';

export interface RefundViaStripeArgs {
  invoiceId: string;
  amount: number;
  reason: string;
  idempotencyKey?: string;
}

export type RefundViaStripeResult =
  | { success: true; data: { invoice_id: string; stripe_refund_id: string; refund_amount: number; total_refunded: number; new_status: string } }
  | { success: false; error: { code: string; meta?: Record<string, unknown> } };

export async function refundViaStripe(args: RefundViaStripeArgs): Promise<RefundViaStripeResult> {
  const { data: sessionData } = await supabase.auth.getSession();
  const accessToken = sessionData.session?.access_token;
  if (!accessToken) return { success: false, error: { code: 'UNAUTHORIZED' } };

  let response: Response;
  try {
    response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/refund-invoice-via-stripe`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
        'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
      },
      body: JSON.stringify({
        invoice_id: args.invoiceId,
        amount: args.amount,
        reason: args.reason,
        ...(args.idempotencyKey ? { idempotency_key: args.idempotencyKey } : {}),
      }),
    });
  } catch (err) {
    return { success: false, error: { code: 'NETWORK_FAILURE', meta: { message: err instanceof Error ? err.message : String(err) } } };
  }

  let payload: unknown;
  try { payload = await response.json(); }
  catch { return { success: false, error: { code: 'INVALID_RESPONSE' } }; }

  const body = payload as { success?: boolean; error?: { code?: string; meta?: Record<string, unknown> }; invoice_id?: string; stripe_refund_id?: string; refund_amount?: number; total_refunded?: number; new_status?: string };
  if (!body.success) {
    return { success: false, error: { code: body.error?.code ?? 'UNKNOWN', meta: body.error?.meta } };
  }
  window.dispatchEvent(new Event(MONEY_MUTATION_EVENT));
  return {
    success: true,
    data: {
      invoice_id: body.invoice_id!,
      stripe_refund_id: body.stripe_refund_id!,
      refund_amount: body.refund_amount!,
      total_refunded: body.total_refunded!,
      new_status: body.new_status!,
    },
  };
}
